import { FleetApiTypes } from 'api'
import { useEffect, useMemo, useState } from 'react'
import {
  PRICE_LIST_DETAILS,
  PRICE_LIST_DETAILS_FORM_VALUES,
} from 'texts/priceListDetails'
import {
  Multiselect,
  Form,
  FormItem,
  Input,
  Textarea,
  Badge,
  Select,
} from '@frontend/design_system'
import { ContentWrapper } from 'ui/atoms/ContentWrapper'
import { FormValues, RateDetailsFormProps } from './types'
import { APPLY_BY, initialFormValues } from './constants'
import { Ranges } from './components/Ranges'
import { useSelectValues } from 'hooks/useSelectValues'
import {
  dailyRangeOverlapValidation,
  seasonRangeOverlapValidation,
  seasonRangeDateValidation,
  seasonRangeEmptyDateValidation,
  dailyRangeGapsValidation,
  dailyRangeEmptyValidation,
  seasonRangeDifferentYearValidation,
  dailyRangeFirstRangeValidation,
  seasonRangeGapValidation,
} from './utils'
import { allSymbolsAndEnglishLettersRegex } from 'constants/regex'
import styles from './styles.module.scss'
import { FormChildren, FormItemChildren } from 'types/form'
import { ALL_COR_ITEMS } from 'constants/countryOfResidence'
import { ApprovalInfoBadges } from 'ui/components/ApprovalInfoBadges/ApprovalInfoBadges'
import { isOperatorSelector, userDataSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { REGISTRATION_STATUSES } from 'api/auth/constants'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'

const COR_RULES = [
  {
    type: 'required',
    message: PRICE_LIST_DETAILS_FORM_VALUES.COR.REQUIRED_MESSAGE,
  },
]

export const PriceListDetailsForm = ({
  handleSubmit,
  initialValues = initialFormValues,
  readOnly,
  locations,
  isEditForm,
  formId,
}: RateDetailsFormProps) => {
  const [formKey, setFormKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { sectionsStatuses } = useAppSelector(userDataSelector)
  const isOperator = useAppSelector(isOperatorSelector)
  const isPendingUser =
    !isOperator &&
    sectionsStatuses?.priceListsStatus !== REGISTRATION_STATUSES.APPROVED

  const locationsSelectValues = useSelectValues(locations)

  const onSubmit = (data: Partial<FormValues>, validate: boolean) => {
    setIsSubmitting(false)
    if (validate) {
      setIsSubmitting(true)
      handleSubmit(data)
    }
  }

  useEffect(() => {
    setFormKey(formKey + 1)
  }, [initialValues])

  const rules = useMemo(
    () => ({
      hirePoints: [
        {
          type: 'required',
          message: PRICE_LIST_DETAILS_FORM_VALUES.HIRE_POINTS.REQUIRED_MESSAGE,
        },
      ],
      year: [
        {
          type: 'required',
          message: PRICE_LIST_DETAILS_FORM_VALUES.YEAR.REQUIRED_MESSAGE,
        },
      ],
      seasonRanges: [
        {
          type: 'required',
          message:
            PRICE_LIST_DETAILS_FORM_VALUES.SEASON_RANGES.REQUIRED_MESSAGE,
        },
        {
          type: 'custom',
          value: seasonRangeDateValidation,
        },
        {
          type: 'custom',
          value: seasonRangeEmptyDateValidation,
        },
        {
          type: 'custom',
          value: seasonRangeOverlapValidation,
        },
        {
          type: 'custom',
          value: seasonRangeDifferentYearValidation,
        },
        {
          type: 'custom',
          value: seasonRangeGapValidation,
        },
      ],
      dailyRanges: [
        {
          type: 'required',
          message: PRICE_LIST_DETAILS_FORM_VALUES.DAILY_RANGES.REQUIRED_MESSAGE,
        },
        {
          type: 'custom',
          value: dailyRangeEmptyValidation,
        },
        {
          type: 'custom',
          value: dailyRangeOverlapValidation,
        },
        {
          type: 'custom',
          value: dailyRangeGapsValidation,
        },
        {
          type: 'custom',
          value: dailyRangeFirstRangeValidation,
        },
      ],
      comment: [
        {
          type: 'pattern',
          value: allSymbolsAndEnglishLettersRegex,
          message: PRICE_LIST_DETAILS_FORM_VALUES.COMMENT.VALIDATION_MESSAGE,
        },
      ],
    }),
    []
  )

  return (
    <Form
      onSubmit={onSubmit}
      initValues={initialValues}
      className={styles.form}
      formId={formId}
      reduxId={formId}
      key={formKey}
    >
      {({ isDirty, values }: FormChildren) => {
        const isYearReadonly =
          readOnly ||
          (
            values[
              PRICE_LIST_DETAILS_FORM_VALUES.SEASON_RANGES.ID
            ] as FleetApiTypes.SeasonRange[]
          ).length
        return (
          <>
            <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
            {isPendingUser && (
              <ApprovalInfoBadges
                isRequested={
                  sectionsStatuses?.priceListsStatus ===
                  REGISTRATION_STATUSES.REQUESTED
                }
              />
            )}
            <ContentWrapper
              className={styles.form}
              title={PRICE_LIST_DETAILS.TITLE}
            >
              <>
                <div className={styles['form-items-row']}>
                  <FormItem
                    id={PRICE_LIST_DETAILS_FORM_VALUES.HIRE_POINTS.ID}
                    className={styles['form-item']}
                    rules={rules.hirePoints}
                  >
                    {({ value, error, onChange }: FormItemChildren) => (
                      <Multiselect
                        error={error}
                        onChange={onChange}
                        items={locationsSelectValues}
                        readonly={readOnly}
                        placeholder={
                          PRICE_LIST_DETAILS_FORM_VALUES.HIRE_POINTS.PLACEHOLDER
                        }
                        label={PRICE_LIST_DETAILS_FORM_VALUES.HIRE_POINTS.LABEL}
                        value={value}
                        isRequired
                        unitName={{
                          plural: PRICE_LIST_DETAILS.SEARCH_VALUE,
                        }}
                      />
                    )}
                  </FormItem>
                  <FormItem
                    id={PRICE_LIST_DETAILS_FORM_VALUES.YEAR.ID}
                    className={styles['form-item']}
                    rules={rules.year}
                  >
                    {({ value, error, onChange }: FormItemChildren) => (
                      <Input
                        value={value}
                        size="large"
                        isRequired
                        placeholder={
                          PRICE_LIST_DETAILS_FORM_VALUES.YEAR.PLACEHOLDER
                        }
                        error={error}
                        maxLength={4}
                        spaceForError="auto"
                        type="integer"
                        isDisabled={isYearReadonly}
                        readonly={isYearReadonly}
                        onChange={onChange}
                        label={PRICE_LIST_DETAILS_FORM_VALUES.YEAR.LABEL}
                      />
                    )}
                  </FormItem>
                </div>
                <div className={styles['form-items-row']}>
                  <FormItem
                    id={PRICE_LIST_DETAILS_FORM_VALUES.COR.ID}
                    rules={COR_RULES}
                    className={styles['form-item']}
                  >
                    {({ value, error, onChange }: FormItemChildren) => (
                      <Multiselect
                        value={value}
                        placeholder={
                          PRICE_LIST_DETAILS_FORM_VALUES.COR.PLACEHOLDER
                        }
                        error={error}
                        tooltip={PRICE_LIST_DETAILS_FORM_VALUES.COR.TOOLTIP}
                        items={ALL_COR_ITEMS}
                        onChange={onChange}
                        label={PRICE_LIST_DETAILS_FORM_VALUES.COR.LABEL}
                        isRequired
                        disabled={readOnly}
                      />
                    )}
                  </FormItem>
                  <div className={styles['form-item']} />
                </div>
              </>
            </ContentWrapper>
            <ContentWrapper
              className={styles.form}
              title={PRICE_LIST_DETAILS.SEASON_RANGE}
              tooltipContent={
                PRICE_LIST_DETAILS_FORM_VALUES.SEASON_RANGES.TOOLTIP
              }
            >
              <FormItem
                id={PRICE_LIST_DETAILS_FORM_VALUES.SEASON_RANGES.ID}
                className={styles['form-item']}
                rules={rules.seasonRanges}
              >
                {({ value, onChange, error }: FormItemChildren) => (
                  <Ranges<FleetApiTypes.SeasonRange>
                    currentYear={
                      values[PRICE_LIST_DETAILS_FORM_VALUES.YEAR.ID] as string
                    }
                    ranges={value}
                    readOnly={readOnly}
                    onRangesChange={onChange}
                    type="season"
                    error={error}
                  />
                )}
              </FormItem>
            </ContentWrapper>
            <ContentWrapper
              className={styles.form}
              title={PRICE_LIST_DETAILS.DAILY_RANGE}
              tooltipContent={
                PRICE_LIST_DETAILS_FORM_VALUES.DAILY_RANGES.TOOLTIP
              }
            >
              <FormItem
                id={PRICE_LIST_DETAILS_FORM_VALUES.DAILY_RANGES.ID}
                className={styles['form-item']}
                rules={rules.dailyRanges}
              >
                {({ value, onChange, error }: FormItemChildren) => (
                  <Ranges<FleetApiTypes.DailyRange>
                    ranges={value}
                    readOnly={readOnly}
                    onRangesChange={onChange}
                    type="daily"
                    error={error}
                  />
                )}
              </FormItem>
            </ContentWrapper>
            <ContentWrapper className={styles.form}>
              <>
                <FormItem
                  id={PRICE_LIST_DETAILS_FORM_VALUES.PRICE_LIST_TYPE.ID}
                >
                  {({ value, onChange }: FormItemChildren) => (
                    <>
                      <div className={styles['price-list-type-select']}>
                        <Select
                          items={Object.entries(APPLY_BY).map(
                            ([key, value]) => ({
                              label: value,
                              value: key,
                            })
                          )}
                          disabled={readOnly}
                          selectedValue={value}
                          noSpaceForError
                          onChange={onChange}
                          label={
                            PRICE_LIST_DETAILS_FORM_VALUES.PRICE_LIST_TYPE.LABEL
                          }
                          size="large"
                        />
                      </div>
                      {isEditForm && value !== initialValues.priceListType && (
                        <Badge
                          type="warning"
                          message={
                            PRICE_LIST_DETAILS_FORM_VALUES.PRICE_LIST_TYPE.BADGE
                          }
                          className={styles['price-list-type-badge']}
                        />
                      )}
                    </>
                  )}
                </FormItem>
                <FormItem
                  id={PRICE_LIST_DETAILS_FORM_VALUES.COMMENT.ID}
                  rules={rules.comment}
                >
                  {({ value, onChange, error }: FormItemChildren) => (
                    <Textarea
                      value={value}
                      disabled={readOnly}
                      onChange={onChange}
                      maxLength={250}
                      label={PRICE_LIST_DETAILS_FORM_VALUES.COMMENT.LABEL}
                      placeholder={
                        PRICE_LIST_DETAILS_FORM_VALUES.COMMENT.PLACEHOLDER
                      }
                      error={error}
                    />
                  )}
                </FormItem>
              </>
            </ContentWrapper>
          </>
        )
      }}
    </Form>
  )
}
